import { Directive, ElementRef, forwardRef, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import flatpickr from 'flatpickr';
import { Portuguese } from 'flatpickr/dist/l10n/pt';
import { Instance } from 'flatpickr/dist/types/instance';

import { avisoPeriodoDataPlugin, mascaraDataPlugin, parseDataEstrita } from 'app/utils/flatpickr-data-estrita';

/**
 * Padroniza os campos nativos de data (type="date") e data/hora (type="datetime-local")
 * no padrão do campo Envio Draft Armador: exibição dd/mm/aaaa (ou dd/mm/aaaa HH:mm),
 * calendário aberto ao clicar no campo e digitação manual.
 *
 * O valor do formulário continua no formato do input nativo (yyyy-MM-dd ou yyyy-MM-ddTHH:mm).
 */
@Directive({
  selector: 'input[type="date"], input[type="datetime-local"]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CoreDateInputDirective),
      multi: true
    }
  ]
})
export class CoreDateInputDirective implements ControlValueAccessor, OnChanges, OnInit, OnDestroy {
  @Input() min: string;
  @Input() max: string;
  @Input() value: string | Date;

  private _input: HTMLInputElement;
  private _flatpickr: Instance;
  private _formatoValor: string;
  private _formatoExibicao: string;
  private _valor: string | Date;

  private _onChange: (valor: string) => void = () => {};
  private _onTouched: () => void = () => {};

  constructor(elementRef: ElementRef<HTMLInputElement>) {
    this._input = elementRef.nativeElement;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.value) {
      this.writeValue(this.value);
    }

    if (!this._flatpickr) {
      return;
    }

    if (changes.min) {
      this._flatpickr.set('minDate', this._converterData(this.min));
    }

    if (changes.max) {
      this._flatpickr.set('maxDate', this._converterData(this.max));
    }
  }

  ngOnInit(): void {
    const comHora = this._input.type === 'datetime-local';

    this._formatoValor = comHora ? 'Y-m-d\\TH:i' : 'Y-m-d';
    this._formatoExibicao = comHora ? 'd/m/Y H:i' : 'd/m/Y';

    this._input.type = 'text';
    this._input.placeholder = comHora ? 'dd/mm/aaaa hh:mm' : 'dd/mm/aaaa';
    this._input.autocomplete = 'off';

    this._flatpickr = flatpickr(this._input, {
      locale: Portuguese,
      dateFormat: this._formatoExibicao,
      enableTime: comHora,
      time_24hr: true,
      allowInput: true,
      clickOpens: false,
      disableMobile: true,
      parseDate: parseDataEstrita,
      plugins: [mascaraDataPlugin, avisoPeriodoDataPlugin],
      minDate: this._converterData(this.min),
      maxDate: this._converterData(this.max),
      onChange: ([data]) => this._onChange(data ? this._flatpickr.formatDate(data, this._formatoValor) : '')
    });

    // O tema remove o fundo de somente leitura dos inputs com esta classe
    this._input.classList.remove('flatpickr-input');

    this._exibirValor();
  }

  ngOnDestroy(): void {
    this._flatpickr?.destroy();
  }

  writeValue(valor: string | Date): void {
    this._valor = valor;
    this._exibirValor();
  }

  registerOnChange(fn: (valor: string) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(desabilitado: boolean): void {
    this._input.disabled = desabilitado;
  }

  @HostListener('click')
  @HostListener('focus')
  abrirCalendario(): void {
    if (!this._input.readOnly && !this._input.disabled) {
      this._flatpickr?.open();
    }
  }

  /**
   * Converte o texto digitado antes dos handlers (blur) do template, para que eles já
   * recebam o valor atualizado, como acontece no input nativo.
   */
  @HostListener('blur', ['$event'])
  confirmarDigitacao(evento: FocusEvent): void {
    if (!this._flatpickr || this._flatpickr.calendarContainer?.contains(evento.relatedTarget as Node)) {
      return;
    }

    const [dataSelecionada] = this._flatpickr.selectedDates;
    const textoSelecionado = dataSelecionada ? this._flatpickr.formatDate(dataSelecionada, this._formatoExibicao) : '';

    if (this._input.value.trim() !== textoSelecionado) {
      this._flatpickr.setDate(this._input.value.trim(), true, this._formatoExibicao);
    }

    this._onTouched();
  }

  /**
   * O input nativo só emite input/change com a data completa. Os eventos da digitação são
   * descartados; os disparados pelo flatpickr (não confiáveis), após atualizar o valor, seguem.
   */
  @HostListener('input', ['$event'])
  @HostListener('change', ['$event'])
  descartarEventoDigitacao(evento: Event): void {
    if (evento.isTrusted) {
      evento.stopImmediatePropagation();
    }
  }

  /**
   * min/max chegam no formato do input nativo; como texto, o flatpickr os leria com o formato
   * de exibição (dd/mm/aaaa). Por isso são convertidos para Date antes de serem repassados.
   */
  private _converterData(valor: string): Date | undefined {
    return valor ? parseDataEstrita(valor, this._formatoValor) : undefined;
  }

  private _exibirValor(): void {
    if (!this._flatpickr) {
      return;
    }

    const data = this._valor ? this._flatpickr.parseDate(this._valor, this._formatoValor) : undefined;

    if (!data) {
      this._flatpickr.clear(false);
      return;
    }

    // Valores fora de min/max continuam visíveis, como no input nativo
    this._flatpickr.config.allowInvalidPreload = true;
    this._flatpickr.setDate(data, false);
    this._flatpickr.config.allowInvalidPreload = false;
  }
}
